import React, {useState} from "react"
import {AsyncResultModal} from "../AsyncResultModal/AsyncResultModal"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useAccessToken} from "../../services/auth"
import {
	returnErroredIncomingAch,
	IncomingAch,
	IncomingAchDishonoredReturnReason,
	IncomingAchContestedDishonoredReturnReason,
	IncomingAchRejectReason,
	isReturnAch,
} from "../../resources/incomingAch"
import {AddToast} from "react-toast-notifications"
import {NavigateFunction, Location} from "react-router-dom"

const modalExitDelay = 1000

interface ReturnErroredIncomingAchModalProps {
	incomingAch: IncomingAch
	close: () => void
	onSuccess: () => void
	addToast: AddToast
	navigate: NavigateFunction
	location: Location
}

function ReasonCode({
	reasonCode,
	setReasonCode,
	transactionCode,
}: {
	reasonCode: string
	setReasonCode: (reasonCode: string) => void
	transactionCode: number
}) {
	return (
		<div className="field">
			<label className="label">Reason</label>
			<div className="select">
				<select
					value={reasonCode}
					onChange={(e) => {
						setReasonCode(e.target.value)
					}}
				>
					{isReturnAch.includes(transactionCode)
						? [
								...Array.from(IncomingAchDishonoredReturnReason.entries()),
								...Array.from(IncomingAchContestedDishonoredReturnReason.entries()),
						  ].map((e) => (
								<option key={e[0]} value={e[0]}>
									{e[0]} - {e[1]}
								</option>
						  ))
						: Array.from(IncomingAchRejectReason.entries()).map((e) => (
								<option key={e[0]} value={e[0]}>
									{e[0]} - {e[1]}
								</option>
						  ))}
				</select>
			</div>
		</div>
	)
}

function refreshAndClose(refresh: () => void, close: () => void) {
	return function () {
		refresh()
		close()
	}
}

function closeAndExit(close: () => void, addToast: AddToast, navigate: NavigateFunction, location: Location) {
	const path = location?.state?.previousPage || "/incoming-achs"
	return function () {
		setTimeout(() => {
			close()
			addToast("ACH Returned", {appearance: "success"})
			navigate && navigate(path)
		}, modalExitDelay)
	}
}

export function ReturnErroredIncomingAchModal({
	incomingAch,
	close,
	onSuccess,
	addToast,
	navigate,
	location,
}: ReturnErroredIncomingAchModalProps) {
	const accessToken = useAccessToken()
	const [reasonCode, setReasonCode] = useState(incomingAch.attributes.reasonCode?.toString() ?? "R61")
	const [state, returnErrored] = useAsyncResultIdle(returnErroredIncomingAch)

	return (
		<AsyncResultModal
			classname={"returned-error-modal"}
			title="Return dishonored Incoming ACH"
			secondaryTitle="Use a dishonor reason only when performing a dishonored return. Use a contested-dishonor reason only when performing a contested dishonored return— - the item being returned was previously dishonored."
			close={refreshAndClose(onSuccess, close)}
			buttonClassname="is-danger"
			state={state}
			buttonText="Return"
			successText="Returned"
			onSuccess={closeAndExit(close, addToast, navigate, location)}
			onSubmit={() => returnErrored(accessToken, incomingAch.id, reasonCode)}
			errorToText={(err) => err.errors[0].title}
		>
			<div className="field">
				<ReasonCode
					reasonCode={reasonCode}
					setReasonCode={setReasonCode}
					transactionCode={incomingAch.attributes.item.transactionCode}
				/>
			</div>
		</AsyncResultModal>
	)
}
